import { useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { Navigate, useLocation } from "react-router";
import { LoadingSpinner } from "../ui/loading-spinner";
import { profileApi } from "@/api/profile-api";
import posthog from "posthog-js";

export function ClerkProtectedRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isSignedIn, isLoaded, user } = useUser();
  const location = useLocation();
  const identifiedRef = useRef(false);

  // Identify user in PostHog using MongoDB _id (must match backend distinct ID)
  useEffect(() => {
    if (isSignedIn && user && !identifiedRef.current) {
      identifiedRef.current = true;
      profileApi
        .getMe()
        .then(({ user: dbUser }) => {
          const name = [dbUser.firstName, dbUser.lastName]
            .filter(Boolean)
            .join(" ");
          posthog.identify(dbUser.id, {
            email: dbUser.email,
            ...(name && { name }),
          });
          // Link Clerk ID → MongoDB ID so any prior Clerk-keyed profiles merge
          posthog.alias(user.id, dbUser.id);
        })
        .catch(() => {
          // Fallback: use Clerk data if /me fails
          const email = user.primaryEmailAddress?.emailAddress;
          const name = [user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ");
          posthog.identify(user.id, {
            ...(email && { email }),
            ...(name && { name }),
          });
        });
    }
    if (!isSignedIn) {
      identifiedRef.current = false;
    }
  }, [isSignedIn, user]);

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to sign-in if user is not authenticated
  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
